import { ConversationListCacheData, conversationSummariesOf, mapConversationListCache } from 'dataQueries/conversationListCache'
import { ConversationMessageBroadcastDTO, ConversationMessageDTO, ConversationMessageStatus, ConversationMessageType, ConversationStatus, ConversationSummaryDTO, CursorPageDTO } from 'models/conversations'
import { lastMessageAttachmentFields } from './chatAttachments'

/**
 * Machine codes the backend carries on the SYSTEM message posted when a conversation is closed: by a
 * participant (ISSUE-2282), automatically after inactivity (ISSUE-1183), or by a Backbone agent from the
 * Salesforce Case (ISSUE-4286). All three lock the composer.
 */
const CONVERSATION_CLOSED_SYSTEM_CODES: ReadonlySet<string> = new Set(['conversation_closed', 'conversation_closed_by_agent', 'conversation_closed_inactivity'])

export const isConversationClosedNotice = (message: Pick<ConversationMessageBroadcastDTO, 'messageType' | 'content'>) =>
  message.messageType === ConversationMessageType.SYSTEM && CONVERSATION_CLOSED_SYSTEM_CODES.has(message.content)

/** Map a real-time message broadcast to the persisted `ConversationMessageDTO` shape. */
export const broadcastToMessage = (broadcast: ConversationMessageBroadcastDTO): ConversationMessageDTO => ({
  id: broadcast.messageId,
  conversationId: broadcast.conversationId,
  senderId: broadcast.senderId,
  // Carried through so a live assistant reply renders with its own identity immediately, instead of
  // looking like a plain incoming bubble until the next history refetch.
  senderRole: broadcast.senderRole,
  content: broadcast.content,
  clientMessageId: broadcast.clientMessageId,
  type: broadcast.messageType,
  status: broadcast.status,
  source: broadcast.source,
  externalId: broadcast.externalId,
  createdAt: broadcast.createdAt,
  updatedAt: broadcast.updatedAt,
  ...(broadcast.actions !== undefined && { actions: broadcast.actions }),
  ...(broadcast.closePrompt !== undefined && { closePrompt: broadcast.closePrompt }),
  ...(broadcast.senderName && { senderName: broadcast.senderName }),
  ...(broadcast.attachments !== undefined && { attachments: broadcast.attachments }),
  ...(broadcast.feedback && { feedback: broadcast.feedback }),
})

/**
 * Applies a real-time message broadcast to a list of conversation summaries:
 *  - updates the last-message preview and timestamp,
 *  - moves the conversation to the top,
 *  - increments the unread count unless the message is the caller's own or the conversation is open.
 *
 * Returns the original array unchanged when the conversation is not present in the list, or when the
 * message is a SYSTEM notice (e.g. "agent joined") — those must not become the list preview nor bump unread.
 */
export const applyMessageToSummaries = (
  summaries: ConversationSummaryDTO[],
  message: ConversationMessageBroadcastDTO,
  currentUserId: string | undefined,
  openConversationId: string | null
): ConversationSummaryDTO[] => {
  if (message.messageType === ConversationMessageType.SYSTEM) {
    // A "conversation closed" system notice flips the conversation's status in the list cache so the
    // counterparty's UI reflects the closure live (composer locks, item moves to the Closed tab) without
    // waiting for the next list refetch. Other SYSTEM notices (e.g. "agent joined") must not touch the list.
    if (!CONVERSATION_CLOSED_SYSTEM_CODES.has(message.content)) return summaries

    const closedIndex = summaries.findIndex((summary) => summary.id === message.conversationId)
    if (closedIndex === -1) return summaries

    const next = [...summaries]
    next[closedIndex] = { ...next[closedIndex], status: ConversationStatus.CLOSED, closedAt: message.createdAt }
    return next
  }

  const index = summaries.findIndex((summary) => summary.id === message.conversationId)
  if (index === -1) return summaries

  const existing = summaries[index]
  const isOwnMessage = !!currentUserId && message.senderId === currentUserId
  const isOpen = openConversationId === message.conversationId
  const shouldCount = !isOwnMessage && !isOpen

  const updated: ConversationSummaryDTO = {
    ...existing,
    lastMessagePreview: message.content,
    ...lastMessageAttachmentFields(message.attachments),
    lastMessageAt: message.createdAt,
    lastMessageSenderId: message.senderId,
    lastMessageSource: message.source,
    lastMessageSenderKind: undefined,
    unreadCount: shouldCount ? (existing.unreadCount ?? 0) + 1 : existing.unreadCount,
  }

  const next = [...summaries]
  next.splice(index, 1)
  next.unshift(updated)

  return next
}

/**
 * React Query `setQueriesData` updater for a conversation list page. Guards against non-list cache
 * entries that share the same key prefix (e.g. the available-contacts query, whose data is an array,
 * or a conversation messages page, whose data shape is `{ items, hasMore, ... }`).
 */
export const hasConversationLanguage = (
  lists: unknown[],
  conversationId: string
) => lists.some((list) =>
  conversationSummariesOf(list).some((summary) => summary.id === conversationId && !!summary.language))

export const patchConversationListData = (
  message: ConversationMessageBroadcastDTO,
  currentUserId: string | undefined,
  openConversationId: string | null
) => (old: ConversationListCacheData | undefined) =>
  mapConversationListCache(old, (summaries) => applyMessageToSummaries(summaries, message, currentUserId, openConversationId))

export const markConversationClosedInList = (conversationId: string, closedAt: string) =>
  (old: ConversationListCacheData | undefined) => mapConversationListCache(old, (summaries) => {
    const index = summaries.findIndex((summary) => summary.id === conversationId)
    if (index === -1 || summaries[index].status === ConversationStatus.CLOSED) return summaries
    const content = [...summaries]
    content[index] = { ...content[index], status: ConversationStatus.CLOSED, closedAt }
    return content
  })

/**
 * React Query `setQueryData` updater for a conversation thread page. Appends new messages and
 * upserts duplicates (same `id` or `clientMessageId`) so status/timestamp updates land in place.
 * Returns the original page unchanged when the cache hasn't been hydrated yet — the next
 * `useGetMessages` mount will fetch from scratch and pick up the new message.
 */
export const patchConversationThreadData = (broadcast: ConversationMessageBroadcastDTO) =>
  (old: CursorPageDTO<ConversationMessageDTO> | undefined): CursorPageDTO<ConversationMessageDTO> | undefined => {
    if (!old) return old
    const incoming = broadcastToMessage(broadcast)
    const existingIndex = old.items.findIndex(
      (item) => item.id === incoming.id || (item.clientMessageId != null && item.clientMessageId !== '' && item.clientMessageId === incoming.clientMessageId)
    )
    const items =
      existingIndex >= 0
        ? old.items.map((item, index) => (index === existingIndex ? { ...item, ...incoming } : item))
        : [...old.items, incoming]
    return { ...old, items, size: items.length }
  }

/**
 * React Query `setQueryData` updater applying a read-receipt to all outgoing messages whose id is
 * less-than-or-equal-to `lastReadMessageId`. Only messages authored by `currentUserId` are bumped to
 * READ — incoming messages are read state is tracked on the caller side via the unread cursor.
 */
export const patchConversationThreadOnReadReceipt = (
  receipt: ConversationReadReceiptDTO,
  currentUserId: string | undefined
) => (old: CursorPageDTO<ConversationMessageDTO> | undefined): CursorPageDTO<ConversationMessageDTO> | undefined => {
  if (!old || !currentUserId) return old
  // The backend echoes the receipt back to the sender — ignore our own cursor to avoid
  // falsely marking our own outgoing messages as READ before the counterparty reads them.
  if (receipt.userId === currentUserId) return old

  // The thread cache stores messages oldest→newest; find the receipt cursor by id and mark every
  // outgoing message at-or-before it as READ. If the cursor isn't in cache yet (e.g. the read user
  // is referencing a message older than our window), we conservatively no-op.
  const cursorIndex = old.items.findIndex((m) => m.id === receipt.lastReadMessageId)
  if (cursorIndex === -1) return old

  let changed = false
  const items = old.items.map((item, index) => {
    if (index > cursorIndex) return item
    if (item.senderId !== currentUserId) return item
    if (item.status === ConversationMessageStatus.READ) return item
    changed = true
    return { ...item, status: ConversationMessageStatus.READ }
  })
  return changed ? { ...old, items } : old
}
